import { useState } from "react";
import { z } from "zod";
import { CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(5, { message: "Please enter a valid email address." })
  .max(254, { message: "Email must be less than 254 characters." })
  .email({ message: "Please enter a valid email address." });

export const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }

    setStatus("loading");
    const { error: insertError } = await supabase
      .from("newsletter_subscribers")
      .insert({ email: parsed.data, source: "footer" });

    // 23505 = unique violation: already subscribed, treat as success.
    if (insertError && insertError.code !== "23505") {
      setStatus("idle");
      setError("Something went wrong. Please try again or email info@pathfree.com.");
      return;
    }

    setStatus("success");
    setEmail("");
  };

  if (status === "success") {
    return (
      <div
        role="status"
        className="flex items-start gap-2 rounded-md border border-accent/40 bg-accent/10 p-3"
      >
        <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={18} />
        <p className="text-sm text-foreground">
          <strong>You're subscribed.</strong> Thanks for joining — we'll send AiMediQ™ and
          datasyteAI™ updates to your inbox.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-2">
      <label htmlFor="newsletter-email" className="text-sm text-muted-foreground">
        Get AiMediQ™ and datasyteAI™ updates by email.
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          id="newsletter-email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          maxLength={254}
          required
          placeholder="you@hospital.org"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? "newsletter-error" : undefined}
          className="h-12"
        />
        <Button type="submit" variant="outline" className="h-12 shrink-0" disabled={status === "loading"}>
          {status === "loading" ? <Loader2 className="animate-spin" size={18} /> : "Subscribe"}
        </Button>
      </div>
      {error && (
        <p id="newsletter-error" role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        No patient or clinical information, please. Unsubscribe anytime.
      </p>
    </form>
  );
};
